<template>
    <vx-modal
        class="max-w-lg"
        :visible="visible"
        @update:visible="emit('update:visible', $event)"
    >
        <template v-slot:header>Are you sure?</template>

        <p class="text-base text-slate-500 mb-3">Before you Launch the Campaign make sure of the following:</p>

        <ul class="ml-4 list-disc text-base text-slate-500">
            <li>You've chosen the right Campaign Duration</li>
            <li>You added the desired Contacts to the Campaign</li>
            <li>You've created the right set of messages</li>
            <li>Your automations, if needed, are in place</li>
        </ul>

        <vx-alert
            v-if="status === 'suggest_sms_convert'"
            color="info"
            data-test="throughput-warning"
            class="mt-6"
        >
            <p>
                The current audience size is approximately {{ audienceSize }} contacts.
            </p>

            <p>
                Based on your carrier capacity for this campaign, we recommend reducing your audience size to
                <span data-test="throughput-suggestion">{{ availableCapacity }}</span> contacts or less. Alternatively,
                we can automatically convert your campaign to pure SMS, and we estimate you have sufficient capacity to
                send it. Click <a href="#" @click.prevent="convertCampaignToSMS" data-test="convert-campaign-to-sms">here</a> to have us do this for you.
            </p>
        </vx-alert>

        <vx-alert
            v-if="status === 'suggest_contact_support'"
            color="info"
            data-test="throughput-warning"
            class="mt-6"
        >
            <p>
                The current audience size is approximately {{ audienceSize }} contacts.
            </p>

            <p>
                Based on your carrier capacity for this campaign, we recommend reducing your audience size to
                <span data-test="throughput-suggestion">{{ availableCapacity }}</span> contacts or less. To increase your
                carrier capacity, please reach out to your <a href="mailto:support@voxiechat.com">account manager</a>.
            </p>
        </vx-alert>

        <vx-alert
            v-if="status === 'no_campaign_message'"
            color="info"
            data-test="no-campaign-message-warning"
            class="mt-6"
        >
            <p class="mb-2">
                The current audience size is approximately {{ audienceSize }} contacts.
            </p>

            <p>
                We are unable to determine if you have sufficient capacity to send this campaign
                because it has no active messages.
            </p>
        </vx-alert>

        <vx-alert
            v-if="status === 'no_phone_number'"
            color="info"
            data-test="no-phone-number-warning"
            class="mt-6"
        >
            <p class="mb-2">
                The current audience size is approximately {{ audienceSize }} contacts.
            </p>

            <p>
                We are unable to determine if you have sufficient capacity to send this campaign
                because no phone numbers are configured to send on.
            </p>
        </vx-alert>

        <vx-alert
            v-if="status === 'no_action_required'"
            color="primary"
            data-test="no-action-required-info"
            class="mt-6"
        >
            <p class="mb-2">
                The current audience size is approximately {{ audienceSize }} contacts.
            </p>

            <p>
                We estimate you have sufficient capacity to send this campaign.
            </p>
        </vx-alert>

        <vx-alert
            v-if="refreshCampaignFailed"
            color="danger"
            :closeable="false"
            data-test="refresh-campaign-error"
            class="mt-6"
        >
            Failed to load the latest campaign data.

            <template v-slot:right>
                <vx-button
                    color="danger"
                    size="sm"
                    @click="refreshCampaign"
                    data-test="retry-refresh-campaign"
                >
                    Retry
                </vx-button>
            </template>
        </vx-alert>

        <vx-button
            class="mt-8"
            block
            size="lg"
            :disabled="launchDisabled"
            @click="liveMode"
            data-test="liveModeBtn"
        >
            Launch
            <font-awesome-icon widthAuto :icon="faRocketLaunch" />
        </vx-button>
    </vx-modal>
</template>

<script setup>
import { actionsClient, campaignsClient } from '~/services';
import { computed, ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faRocketLaunch } from '@fortawesome/pro-solid-svg-icons';
import { VxButton, VxModal, VxAlert } from '@voxie/frontend-components';
import { useToasted } from '../../composables/useToasted';
import dayjs from '~/utils/dayjs';

const props = defineProps({
    campaign: {
        type: Object,
        required: false,
    },
    visible: Boolean,
});

const emit = defineEmits(['update:visible', 'update:campaign']);

const teamId = Spark.state.currentTeam.id;
const isSubmitting = ref(false);
const isLoadingCampaignCapacity = ref(false);
const refreshingCampaign = ref(false);
const refreshCampaignFailed = ref(false);
const availableCapacity = ref(null);
const audienceSize = ref(null);
const status = ref(null);

const launchDisabled = computed(() => {
    return isSubmitting.value || isLoadingCampaignCapacity.value || refreshingCampaign.value || refreshCampaignFailed.value;
});

const close = () => emit('update:visible', false);

const toasted = useToasted();

watch(() => props.visible, (visible) => {
    if (!visible) {
        isLoadingCampaignCapacity.value = false;
        refreshingCampaign.value = false;
        refreshCampaignFailed.value = false;
        availableCapacity.value = null;
        audienceSize.value = null;
        status.value = null;
        return;
    }

    if (props.campaign?.id) {
        refreshCampaign();
        getCampaignCapacity();
    }
}, {
    immediate: true,
})

const refreshCampaign = async () => {
    refreshingCampaign.value = true;
    refreshCampaignFailed.value = false;
    try {
        const { data } = await campaignsClient.campaignGet(teamId, props.campaign.id);
        emit('update:campaign', data);
    } catch {
        refreshCampaignFailed.value = true;
    } finally {
        refreshingCampaign.value = false;
    }
};

const liveMode = () => {
    changeStatus(props.campaign.live_at && dayjs.utc(props.campaign.live_at).isAfter(dayjs()) ? 'scheduled' : 'published');
}

const changeStatus = (newStatus) => {
    const campaign = { ...props.campaign };
    isSubmitting.value = true;

    campaignsClient.changeStatus(teamId, campaign.id, newStatus)
        .then(({ data }) => {
            if (newStatus === 'published' && campaign.status !== 'published') {
                actionsClient.send('campaign_turned_live');
            }
            campaign.status = newStatus;
            campaign.started_at = data.started_at;
            emit('update:campaign', campaign);
            close();
            toasted.global.saved_successfully();
        })
        .catch(() => toasted.global.platform_error())
        .finally(() => isSubmitting.value = false);
}

const getCampaignCapacity = () => {
    isLoadingCampaignCapacity.value = true;
    campaignsClient.campaignCapacity(teamId, props.campaign.id).then(({ data }) => {
        availableCapacity.value = data.available_capacity != null ? formatNumber(data.available_capacity) : null;
        audienceSize.value = data.audience_size != null ? formatNumber(data.audience_size) : null;
        status.value = data.status;
    })
    .finally(() => isLoadingCampaignCapacity.value = false);
};

const formatNumber = (number) => {
    return Intl.NumberFormat('en-US').format(number);
}

const convertCampaignToSMS = async () => {
    try {
        const response = await campaignsClient.campaignGet(teamId, props.campaign.id);
        const messages = response.data.campaign_messages.map((campaignMessage) => {
            campaignMessage.media_url = null;
            campaignMessage.media_content_type = null;

            return campaignMessage;
        });

        const campaignToUpdate = {
            ...response.data,
            campaign_messages: messages,
        }

        await campaignsClient.campaignSave(teamId, campaignToUpdate);

        getCampaignCapacity();
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    }
}
</script>
